import { BRAND_NAME, INSTAGRAM_URL, INSTAGRAM_HANDLE, CONTACT_EMAIL, api } from "@/lib/api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Instagram, Phone, Mail } from "lucide-react";
import { useEffect, useRef } from "react";

export default function Layout({ children, step }) {
  const navigate = useNavigate();
  const location = useLocation();
  const clicksRef = useRef({ count: 0, t: 0 });
  const trackedRef = useRef("");

  // Fire-and-forget anonymous page-view beacon for non-admin pages.
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin")) return;
    if (trackedRef.current === path) return;
    trackedRef.current = path;
    api.post("/track/visit", { path, referrer: document.referrer || "" }).catch(() => {});
  }, [location.pathname]);

  const handleLogoClick = (e) => {
    // Triple-click within 1.2s opens hidden admin login
    const now = Date.now();
    if (now - clicksRef.current.t > 1200) clicksRef.current.count = 0;
    clicksRef.current.count += 1;
    clicksRef.current.t = now;
    if (clicksRef.current.count >= 3) {
      e.preventDefault();
      clicksRef.current.count = 0;
      navigate("/admin");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="px-5 sm:px-8 pt-6 pb-4 flex items-center justify-between max-w-3xl w-full mx-auto"
        data-testid="site-header"
      >
        <Link to="/" className="block" data-testid="logo-link" onClick={handleLogoClick}>
          <img
            src="/brand/logo.jpeg"
            alt={BRAND_NAME}
            className="h-20 sm:h-24 w-auto rounded-2xl shadow-[0_8px_24px_-8px_rgba(120,40,0,0.45)] select-none"
            draggable={false}
            data-testid="brand-logo"
          />
        </Link>
        {step && (
          <div
            className="label-tag"
            data-testid="step-indicator"
          >
            Stap {step} / 3
          </div>
        )}
      </header>

      <main className="flex-1 px-5 sm:px-8 pb-12 max-w-3xl w-full mx-auto fade-in">
        {children}
      </main>

      <footer
        className="px-5 sm:px-8 py-8 max-w-3xl w-full mx-auto"
        data-testid="site-footer"
      >
        <div className="border-t border-black/30 pt-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
              data-testid="instagram-link"
            >
              <Instagram size={16} />
              <span>@{INSTAGRAM_HANDLE}</span>
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
              data-testid="email-link"
            >
              <Mail size={14} />
              <span>{CONTACT_EMAIL}</span>
            </a>
            <a
              href="tel:+31621278150"
              className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
              data-testid="phone-link"
            >
              <Phone size={14} />
              <span>06 21 27 81 50</span>
            </a>
          </div>
          <div className="label-tag opacity-70 flex items-center gap-3 flex-wrap">
            <span>© {new Date().getFullYear()} {BRAND_NAME}</span>
            <span aria-hidden="true">·</span>
            <Link
              to="/terms"
              className="underline hover:opacity-80"
              data-testid="footer-terms-link"
            >
              Algemene Voorwaarden
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
